/**
 * POIManager — points of interest (POIs)
 */

export default class POIManager {
    constructor(mapManager, block) {
        this.mapManager = mapManager
        this.map = mapManager.getMap()
        this.block = block
        this.markers = new Map()
        this.activeTypes = new Set()
        this.allPois = []
        this.loadedCategories = new Set() // Track categories already fetched
    }

    /**
     * Loads POIs for a category via AJAX
     */
    async loadAllPOIs(category, source) {
        // Skip network if this category was already loaded
        const categoryKey = `${source}-${category}`

        if (this.loadedCategories.has(categoryKey)) {
            // Category cached — show existing markers only
            const pois = this.allPois.filter((poi) => poi.category === category)

            for (let i = 0; i < pois.length; i++) {
                this.addMarker(pois[i])
            }

            // Refresh clusters
            this.updateClusters()

            return
        }

        // First load — fetch via AJAX
        try {
            const response = await fetch(IRISCollectionCustomer.ajaxUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: new URLSearchParams({
                    action: "get_interactive_map_pois",
                    source: source,
                    category: category,
                }),
            })

            const result = await response.json()

            if (!result.success || !result.data || !result.data.pois) {
                console.error("POIManager: Invalid response from server", result)
                return
            }

            // Store POIs
            const pois = result.data.pois

            // Add markers
            for (let i = 0; i < pois.length; i++) {
                this.addMarker(pois[i])
            }

            // Append to client-side cache
            this.allPois.push(...pois)

            // Mark category as loaded
            this.loadedCategories.add(categoryKey)

            // Refresh clusters
            this.updateClusters()
        } catch (error) {
            console.error("POIManager: Error loading POIs", error)
        }
    }

    /**
     * Removes markers for a category (or all if category omitted)
     */
    async removeAllPOIs(category) {
        for (const [key, value] of this.markers) {
            if (category && !category.includes(value.poi.category)) continue
            this.removeMarker(key)
        }

        // Refresh clusters after removal
        this.updateClusters()
    }

    /**
     * Adds a single POI marker on the map
     */
    addMarker(poi) {
        if (!poi.lat || !poi.lng) {
            console.warn(`POIManager: Marker ${poi.id} has no coordinates`)
            return
        }

        if (this.markers.has(poi.id)) {
            console.warn(`POIManager: Marker ${poi.id} already exists`)
            return
        }

        const el = document.createElement("div")
        el.className = `poi-marker poi-marker--${poi.category}`
        el.style.cssText = `
            width: 36px;
            height: 36px;
            background-image: url('${poi.url}');
            background-size: contain;
            background-repeat: no-repeat;
            background-position: center;
            display: 'block';
        `
        el.dataset.poiId = poi.id
        el.dataset.poiType = poi.category

        const marker = this.map.Marker({element: el})

        marker.setLngLat({lng: parseFloat(poi.lng), lat: parseFloat(poi.lat)})

        marker.addEvent("mouseenter", () => {
            el.style.backgroundImage = `url('${poi.urlActive}')`
        })
        marker.addEvent("mouseleave", () => {
            el.style.backgroundImage = `url('${poi.url}')`
        })

        const clusterConfig = {
            infoWindow: poi.popup,
            markerUrl: poi.url,
            offerID: poi.id,
            options: {},
            postType: poi.category,
        }

        Object.assign(marker, clusterConfig)

        // Store marker reference
        this.markers.set(poi.id, {marker, element: el, poi})

        // Collect distinct marker icon URLs for clustering
        if (!this.mapManager.markersUrls.some((obj) => obj.slug === poi.category)) {
            this.mapManager.markersUrls.push({
                slug: poi.category,
                image: poi.url,
            })
        }
    }

    /**
     * Removes one marker from the map
     */
    removeMarker(poiId) {
        try {
            const markerData = this.markers.get(poiId)
            if (!markerData) {
                console.warn(`POIManager: Marker ${poiId} not found`)
                return
            }
            this.markers.delete(poiId)
            markerData.marker.remove()
        } catch (error) {
            console.error(`POIManager: Error removing marker ${poiId}`, error)
        }
    }

    /**
     * Rebuilds clusters from all active markers
     */
    updateClusters() {
        const clusterId = "poi-cluster"

        // Collect active markers
        const markersArray = Array.from(this.markers.values()).map((data) => data.marker)

        // Create or update cluster when markers exist
        if (markersArray.length > 0) {
            this.mapManager.addCluster(clusterId, markersArray)
        } else {
            // No markers — remove cluster layers/sources
            this.mapManager.removeCluster(clusterId)
        }
    }

    /**
     * Toggles POIs by category
     *
     * @param {string} category - POI category slug
     * @param {string} source - Data source (step, post-type, overpass, etc.)
     * @param {boolean} active - Enable or disable the filter
     */
    async filterByType(category, source, active) {
        if (active) {
            this.activeTypes.add(category)
            await this.loadAllPOIs(category, source)
        } else {
            this.activeTypes.delete(category)
            await this.removeAllPOIs(category)
        }

        // Notify the block to refresh active-tag chips
        this.block.dispatchEvent(new CustomEvent("update-active-tags"))
    }

    /**
     * Clears all POI state and clusters
     */
    resetAll() {
        this.activeTypes.clear()
        this.markers.clear()
        this.mapManager.removeCluster("poi-cluster")
    }

    /**
     * Returns active POI filter entries for the tag bar
     *
     * @returns {Array<{id: string, title: string}>}
     */
    getActiveTypes() {
        const activeTypes = []
        for (const type of this.activeTypes) {
            const input = this.block.querySelector(`input[data-filter-key][value="${type}"]`)
            const label = input ? input.nextElementSibling.textContent.trim() : type
            activeTypes.push({
                id: type,
                title: label,
            })
        }
        return activeTypes
    }
}
